import React, { useEffect, useState, useLayoutEffect } from "react";
import { Icon, TooltipComponent } from "../../components/Component";
import {
  Badge,
  DropdownItem,
  DropdownMenu,
  DropdownToggle,
  UncontrolledDropdown,
  Alert,
} from "reactstrap";
import SimpleBar from "simplebar-react";
import miscService from "../../api/misc/misc";
import dayjs from "dayjs";
import { Flex, message, Spin } from "antd";
import InboxMessages from "./InboxMessages";
import Swal from "sweetalert2";
import { useLocation } from "react-router";

const moduleColors: Record<string, string> = {
  "Work Boq": "primary",
  "Work Order": "info",
  "RA Bill": "success",
  "Work Execution": "warning",
  Payments: "danger",
  Default: "light",
};

// Module-level variables to persist across component mounts/unmounts
let savedMailId: number | null = null;
let isNavigatingAway: boolean = false;
let savedCurrentTab: string | null = null;
let isRestoring: boolean = false;

const NotificationList: React.FC<any> = ({
  currentTab,
  setCounts,
  setCurrentTab,
}) => {
  const [check, setChecked] = useState(false);
  const [selectedIds, setSelectedIds] = useState<Set<number>>(new Set());
  const [refresh, setRefresh] = useState(false);
  const [tabData, setTabData] = useState<any>([]);
  const [searchText, setSearchText] = useState("");
  const [searchToggle, setSearchToggle] = useState(false);
  const [totalItems, setTotalItems] = useState(0);
  const [pagination, setPagination] = useState({
    currentPage: 1,
    pageSize: 9,
    direction: "P",
    paginationId: null,
    pageDiff: 0,
  });
  const [loading, setLoading] = useState<boolean>(false);
  const [mailId, setMailId] = useState(1);
  const [messageView, setMessageView] = useState(false);
  const [filter, setFilter] = useState("All");
  const isInitialMount = React.useRef(true);
  const prevTabRef = React.useRef(currentTab);
  const searchInputRef = React.useRef<HTMLInputElement>(null);
  const searchTextRef = React.useRef<string>("");
  const lastSearchQueryRef = React.useRef<string>("");
  const location = useLocation();

  // Map module names to module IDs for WOM
  const moduleIdMap: Record<string, number> = {
    "Work Boq": 125,
    "Work Order": 127,
    "RA Bill": 128,
    "Work Execution": 176,
    Payments: 16,
  };

  const handleFilterChange = (moduleName: string, moduleId: any) => {
    setFilter(moduleName);
    const params: any = { ...pagination };
    // Reset pagination when filter changes
    params.currentPage = 1;
    params.paginationId = null;
    params.pageDiff = 0;

    // For "All", don't pass moduleId
    if (moduleName !== "All") {
      // Use moduleId if provided, otherwise get from map
      const idToUse =
        moduleId !== null && moduleId !== undefined
          ? moduleId
          : moduleIdMap[moduleName];

      if (idToUse !== undefined && idToUse !== null) {
        params.moduleId = idToUse;
      }
      // Remove moduleName if it exists (we only use moduleId)
      if (params.moduleName !== undefined) {
        delete params.moduleName;
      }
    } else {
      // For "All", remove moduleId and moduleName
      if (params.moduleId !== undefined) {
        delete params.moduleId;
      }
      if (params.moduleName !== undefined) {
        delete params.moduleName;
      }
    }
    fetchNotificationList(params, currentTab);
  };

  useEffect(() => {
    notificationCounts();
    // Check on mount if we need to restore
    if (
      location.pathname.includes("/notifications") &&
      isNavigatingAway &&
      savedMailId !== null
    ) {
      isRestoring = true;

      // Restore the message view and mailId first to prevent flash
      const mailIdToRestore = savedMailId;
      const tabToRestore = savedCurrentTab;

      if (mailIdToRestore !== null) {
        setMailId(mailIdToRestore);
        setMessageView(true);
      }

      // Restore the tab after setting messageView to prevent it from being reset
      if (tabToRestore && setCurrentTab) {
        // Use setTimeout to ensure messageView is set first
        setTimeout(() => {
          setCurrentTab(tabToRestore);
          // Clear the restoration flag after tab is set
          setTimeout(() => {
            isRestoring = false;
          }, 100);
        }, 0);
      } else {
        isRestoring = false;
      }

      // Clear the module-level variables after restoring
      savedMailId = null;
      isNavigatingAway = false;
      savedCurrentTab = null;
    }
  }, []);

  // Restore InboxMessages view when navigating back from a notification redirect
  useEffect(() => {
    // Check if we're on the notifications page and have saved state
    if (
      location.pathname.includes("/notifications") &&
      isNavigatingAway &&
      savedMailId !== null
    ) {
      isRestoring = true;

      // Restore the message view and mailId first to prevent flash
      const mailIdToRestore = savedMailId;
      const tabToRestore = savedCurrentTab;

      if (mailIdToRestore !== null) {
        setMailId(mailIdToRestore);
        setMessageView(true);
      }

      // Restore the tab after setting messageView to prevent it from being reset
      if (tabToRestore && setCurrentTab) {
        // Use setTimeout to ensure messageView is set first
        setTimeout(() => {
          setCurrentTab(tabToRestore);
          // Clear the restoration flag after tab is set
          setTimeout(() => {
            isRestoring = false;
          }, 100);
        }, 0);
      } else {
        isRestoring = false;
      }

      // Clear the module-level variables after restoring
      savedMailId = null;
      isNavigatingAway = false;
      savedCurrentTab = null;
    }
  }, [location.pathname, setCurrentTab]);

  // Function to save mailId and currentTab before navigation (passed to InboxMessages)
  const saveMailIdBeforeNavigation = (id: number) => {
    savedMailId = id;
    savedCurrentTab = currentTab;
    isNavigatingAway = true;
  };

  const notificationCounts = async () => {
    try {
      const { response, status } = await miscService.notificationCount({
        appType: "WOM",
      });
      if (status) {
        setCounts({
          fyiCount: response.fyiCount,
          fyaCount: response.fyaCount,
        });
      }
    } catch (err) {
      console.log("err", err);
    }
  };

  // Handle select all checkbox
  const handleSelectAll = (checked: boolean) => {
    setChecked(checked);
    if (checked) {
      const allIds = new Set<number>(
        tabData.map((item: any) => item.notificationId as number)
      );
      setSelectedIds(allIds);
    } else {
      setSelectedIds(new Set<number>());
    }
  };

  // Handle individual checkbox change
  const handleCheckboxChange = (notificationId: number, checked: boolean) => {
    const newSelectedIds = new Set(selectedIds);
    if (checked) {
      newSelectedIds.add(notificationId);
    } else {
      newSelectedIds.delete(notificationId);
    }
    setSelectedIds(newSelectedIds);
    // Update select all checkbox based on current selection
    setChecked(newSelectedIds.size === tabData.length && tabData.length > 0);
  };

  // Delete selected notifications
  const deleteSelectedNotifications = async () => {
    if (selectedIds.size === 0) {
      message.warning("Please select at least one notification to delete");
      return;
    }

    Swal.fire({
      title: "Are you sure?",
      text: `You are about to delete ${selectedIds.size} notification(s). This action cannot be undone!`,
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#3085d6",
      cancelButtonColor: "#d33",
      confirmButtonText: "Yes, delete them!",
      cancelButtonText: "Cancel",
    }).then(async (result) => {
      if (result.isConfirmed) {
        const idsArray = Array.from(selectedIds);
        try {
          const { status } = await miscService.deleteNotification(idsArray);
          if (status) {
            Swal.fire({
              title: "Deleted!",
              text: `Successfully deleted ${idsArray.length} notification(s)`,
              icon: "success",
              timer: 2000,
              showConfirmButton: false,
            });
            // Clear selection
            setSelectedIds(new Set());
            setChecked(false);
            // Refresh the list
            setRefresh(true);
            // Refresh counts
            notificationCounts();
          } else {
            message.error("Failed to delete notification(s)");
          }
        } catch (err) {
          console.log("err", err);
          message.error("Error deleting notification(s)");
        }
      }
    });
  };

  // Delete single notification
  const deleteNotification = async (notificationId: number) => {
    Swal.fire({
      title: "Are you sure?",
      text: "You won't be able to revert this!",
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#3085d6",
      cancelButtonColor: "#d33",
      confirmButtonText: "Yes, delete it!",
      cancelButtonText: "Cancel",
    }).then(async (result) => {
      if (result.isConfirmed) {
        try {
          const { status } = await miscService.deleteNotification([
            notificationId,
          ]);
          if (status) {
            Swal.fire({
              title: "Deleted!",
              text: "Successfully deleted notification",
              icon: "success",
              timer: 2000,
              showConfirmButton: false,
            });
            // Remove from selection if selected
            const newSelectedIds = new Set(selectedIds);
            newSelectedIds.delete(notificationId);
            setSelectedIds(newSelectedIds);
            // Refresh the list
            setRefresh(true);
            // Refresh counts
            notificationCounts();
          } else {
            message.error("Failed to delete notification");
          }
        } catch (err) {
          console.log("err", err);
          message.error("Error deleting notification");
        }
      }
    });
  };

  // Update select all checkbox when tabData changes
  useEffect(() => {
    if (tabData.length > 0 && selectedIds.size === tabData.length) {
      const allSelected = tabData.every((item: any) =>
        selectedIds.has(item.notificationId)
      );
      setChecked(allSelected);
    } else if (selectedIds.size === 0) {
      setChecked(false);
    }
  }, [tabData, selectedIds]);

  // Handle currentTab changes - reset pagination when tab changes
  useEffect(() => {
    // Skip initial mount
    if (isInitialMount.current) {
      isInitialMount.current = false;
      return;
    }

    // Reset pagination when tab changes
    const resetPagination = {
      currentPage: 1,
      pageSize: 9,
      direction: "P",
      paginationId: null,
      pageDiff: 0,
    };
    setPagination(resetPagination);
    // Fetch will be triggered by pagination useEffect when state updates
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentTab]);

  // Fetch on pagination/refresh changes or initial mount
  useEffect(() => {
    fetchNotificationList(pagination, currentTab);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pagination, refresh]);

  useEffect(() => {
    // Only reset messageView if we're not restoring from navigation
    if (!isRestoring) {
      setMessageView(false);
    }
  }, [currentTab]);

  const fetchNotificationList = async (paginationParams: any, tab: any) => {
    setLoading(true);
    const params: any = {
      ...paginationParams,
      sortOrder: "DESC",
      actionRequired: tab === "For Your Information" ? 0 : 1,
      appType: "WOM",
    };
    // Add search query if search text exists
    const currentSearchQuery =
      searchText && searchText.trim() ? searchText.trim() : "";
    if (currentSearchQuery) {
      params.searchQuery = currentSearchQuery;
    }

    // Store the search query that was used in this API call
    lastSearchQueryRef.current = currentSearchQuery;

    try {
      const { response, status } = await miscService.notificationList(params);
      if (status) {
        const { list, count } = response;
        setTabData(list);
        setTotalItems(count);
      }
    } catch (err) {
      console.log("err", err);
    } finally {
      setLoading(false);
    }
  };

  // Keep focus on search input after data loads - use useLayoutEffect for immediate focus
  useLayoutEffect(() => {
    if (!loading && searchToggle && searchInputRef.current) {
      // Focus immediately after DOM updates
      const input = searchInputRef.current;
      const cursorPosition = input.selectionStart || searchText.length;
      input.focus();
      // Restore cursor position
      if (input.setSelectionRange) {
        input.setSelectionRange(cursorPosition, cursorPosition);
      }
    }
  }, [loading, searchToggle, searchText]);

  const toggleRefresh = () => {
    setRefresh(true);
    setTimeout(() => setRefresh(false), 3000);
  };

  const onSearchChange = (e: any) => {
    let value = e.target.value;
    setSearchText(value);
  };

  const handleSearchBlur = (e: React.FocusEvent<HTMLInputElement>) => {
    // Don't refocus if user clicked outside the search area
    const relatedTarget = e.relatedTarget as HTMLElement;
    if (relatedTarget && relatedTarget.closest(".search-wrap")) {
      return;
    }
    // Only refocus if search toggle is still active and there's search text
    // and the blur wasn't intentional (user clicking outside)
    if (searchToggle && searchText && searchInputRef.current) {
      // Small delay to check if focus moved to another element
      setTimeout(() => {
        if (
          document.activeElement !== searchInputRef.current &&
          !document.activeElement?.closest(".nk-ibx-list")
        ) {
          searchInputRef.current?.focus();
        }
      }, 50);
    }
  };

  // Debounce search - trigger fetch after user stops typing
  useEffect(() => {
    // If search text is cleared, reset pagination immediately without debounce
    if (!searchText || searchText.trim() === "") {
      // Only reset if we had search text before (to avoid reset on initial mount)
      const hadSearchText =
        searchTextRef.current && searchTextRef.current.trim() !== "";
      if (hadSearchText) {
        searchTextRef.current = "";
        // Only reset pagination if we were actually using a search query
        // This prevents unnecessary API call if search was already cleared
        if (
          lastSearchQueryRef.current &&
          lastSearchQueryRef.current.trim() !== ""
        ) {
          // Reset pagination immediately when search is cleared
          setPagination({
            currentPage: 1,
            pageSize: 9,
            direction: "P",
            paginationId: null,
            pageDiff: 0,
          });
          // This will trigger fetchNotificationList via pagination useEffect
          // but without searchQuery param since searchText is now empty
        }
      } else {
        // On initial mount with empty search, just store it
        searchTextRef.current = "";
      }
      return;
    }

    // Store current search text in ref
    searchTextRef.current = searchText;

    const searchTimer = setTimeout(() => {
      // Reset pagination and trigger search
      setPagination({
        currentPage: 1,
        pageSize: 9,
        direction: "P",
        paginationId: null,
        pageDiff: 0,
      });
      // Refocus after pagination reset
      if (searchInputRef.current) {
        setTimeout(() => {
          searchInputRef.current?.focus();
        }, 50);
      }
    }, 500); // Wait 500ms after user stops typing

    return () => clearTimeout(searchTimer);
  }, [searchText]);

  const toggleSearch = () => {
    setSearchToggle(!searchToggle);
    if (!searchToggle) {
      // Focus input when search is opened
      setTimeout(() => {
        searchInputRef.current?.focus();
      }, 100);
    }
  };
  const [aside, setAside] = useState(false);
  const pageNumbers: any = [];

  // for (let i = 1; i <= Math.ceil(totalItems / itemPerPage); i++) {
  //   pageNumbers.push(i);
  // }

  const handlePageChange = (newPage: number) => {
    const isForward = newPage > pagination.currentPage;
    const direction = isForward ? "F" : "B";
    const pageDiff = Math.abs(newPage - pagination.currentPage) - 1;
    const paginationId = isForward
      ? tabData[tabData.length - 1]?.paginationId // Use last item ID for forward
      : tabData[0]?.paginationId; // Use first item ID for backward

    setPagination({
      ...pagination,
      currentPage: newPage,
      direction,
      pageDiff,
      paginationId,
    });
  };

  const nextPage = () => {
    if (pagination.currentPage < Math.ceil(totalItems / pagination.pageSize)) {
      handlePageChange(pagination.currentPage + 1);
    }
  };

  const prevPage = () => {
    if (pagination.currentPage > 1) {
      handlePageChange(pagination.currentPage - 1);
    }
  };

  const onListClick = async (ev: any, item: any) => {
    console.log("item", item);
    ev.preventDefault();
    toggleMessageView();
    setMailId(item.notificationId);
    toggleUnread(item.notificationId);
    notificationCounts();
    setTabData((prevTabData: any) =>
      prevTabData.map((i: any) =>
        i.notificationId === item.notificationId ? { ...i, viewed: true } : i
      )
    );

    try {
      await messageRead(item.notificationId);
      // If the API call is successful, we already updated the UI optimistically
    } catch (error) {
      console.error("Error marking notification as read:", error);
      // Optionally, revert the change in case of an API failure
    }
  };

  const messageRead = async (id: any) => {
    try {
      const { response, status } = await miscService.notificationRead(id, {
        appType: "WOM",
      });
      if (status) {
      }
    } catch (err) {
      console.log("err", err);
    }
  };

  const toggleMessageView = () => {
    setMessageView(!messageView);
    // Clear saved mailId when manually closing message view
    if (messageView) {
      savedMailId = null;
      isNavigatingAway = false;
      savedCurrentTab = null;
    }
  };

  const toggleUnread = (id: any) => {
    // let defaultData = data;
    // let item = defaultData.findIndex((item) => item.id === id);
    // defaultData[item].message.meta.unread = true;
    // setData([...defaultData]);
  };

  return (
    <div className="nk-ibx-body bg-white">
      {messageView ? (
        <InboxMessages
          mailId={mailId}
          toggleMessageView={toggleMessageView}
          saveMailIdBeforeNavigation={saveMailIdBeforeNavigation}
        />
      ) : (
        <>
          {loading ? (
            <>
              <Flex align="center" gap="middle" className="pageLoader">
                <Spin size="large" className="m-auto" />
              </Flex>
            </>
          ) : (
            <React.Fragment>
              <div className="nk-ibx-head" style={{ minHeight: "75px" }}>
                <div className="nk-ibx-head-actions">
                  <div className="nk-ibx-head-check">
                    <div className="custom-control custom-control-sm custom-checkbox">
                      <input
                        type="checkbox"
                        checked={check}
                        disabled={tabData.length === 0}
                        className="custom-control-input nk-dt-item-check"
                        id="conversionAll"
                        onChange={(e) => handleSelectAll(e.target.checked)}
                      />
                      <label
                        className="custom-control-label"
                        htmlFor="conversionAll"
                      ></label>
                    </div>
                  </div>
                  <ul className="nk-ibx-head-tools g-1">
                    <li>
                      <a
                        href="#item"
                        onClick={(ev) => {
                          ev.preventDefault();
                          toggleRefresh();
                        }}
                        className="btn btn-icon btn-trigger"
                      >
                        <Icon name="undo"></Icon>
                      </a>
                    </li>

                    <li className="d-none d-sm-block">
                      <a
                        href="#item"
                        onClick={(ev) => {
                          ev.preventDefault();
                          if (selectedIds.size > 0) {
                            deleteSelectedNotifications();
                          }
                        }}
                        className={`btn btn-icon btn-trigger ${
                          selectedIds.size === 0 ? "disabled" : ""
                        }`}
                        style={{
                          opacity: selectedIds.size === 0 ? 0.5 : 1,
                          cursor:
                            selectedIds.size === 0 ? "not-allowed" : "pointer",
                        }}
                      >
                        <Icon name="trash"></Icon>
                      </a>
                    </li>
                  </ul>
                </div>
                <div>
                  <ul className="nk-ibx-head-tools g-1">
                    <li>
                      <a
                        href="#prev"
                        onClick={(ev) => {
                          ev.preventDefault();
                          prevPage();
                        }}
                        className={`btn btn-trigger btn-icon btn-tooltip ${
                          pagination.currentPage === 1 ? "disabled" : ""
                        }`}
                      >
                        <Icon name="arrow-left"></Icon>
                      </a>
                    </li>
                    <li>
                      <a
                        href="#next"
                        onClick={(ev) => {
                          ev.preventDefault();
                          nextPage();
                        }}
                        className={`btn btn-trigger btn-icon btn-tooltip ${
                          pagination.currentPage ===
                          Math.ceil(totalItems / pagination.pageSize)
                            ? "disabled"
                            : ""
                        }`}
                      >
                        <Icon name="arrow-right"></Icon>
                      </a>
                    </li>
                    <li>
                      <a
                        href="#item"
                        onClick={(ev) => {
                          ev.preventDefault();
                          toggleSearch();
                        }}
                        className="btn btn-trigger btn-icon search-toggle toggle-search"
                      >
                        <Icon name="search"></Icon>
                      </a>
                    </li>
                    <li className="me-n1">
                      <UncontrolledDropdown>
                        <DropdownToggle
                          tag="a"
                          href="#item"
                          onClick={(ev) => ev.preventDefault()}
                          className="btn btn-trigger btn-icon toggle"
                        >
                          <Icon name="filter" />
                        </DropdownToggle>
                        <DropdownMenu end>
                          <DropdownItem
                            onClick={() => handleFilterChange("All", undefined)}
                            className={filter === "All" ? "active" : ""}
                          >
                            All
                          </DropdownItem>
                          <DropdownItem
                            onClick={() =>
                              handleFilterChange(
                                "Work Boq",
                                moduleIdMap["Work Boq"]
                              )
                            }
                            className={filter === "Work Boq" ? "active" : ""}
                          >
                            Work Boq
                          </DropdownItem>
                          <DropdownItem
                            onClick={() =>
                              handleFilterChange(
                                "Work Order",
                                moduleIdMap["Work Order"]
                              )
                            }
                            className={filter === "Work Order" ? "active" : ""}
                          >
                            Work Order
                          </DropdownItem>
                          <DropdownItem
                            onClick={() =>
                              handleFilterChange(
                                "RA Bill",
                                moduleIdMap["RA Bill"]
                              )
                            }
                            className={filter === "RA Bill" ? "active" : ""}
                          >
                            RA Bill
                          </DropdownItem>
                          <DropdownItem
                            onClick={() =>
                              handleFilterChange(
                                "Work Execution",
                                moduleIdMap["Work Execution"]
                              )
                            }
                            className={
                              filter === "Work Execution" ? "active" : ""
                            }
                          >
                            Work Execution
                          </DropdownItem>
                          <DropdownItem
                            onClick={() =>
                              handleFilterChange(
                                "Payments",
                                moduleIdMap["Payments"]
                              )
                            }
                            className={filter === "Payments" ? "active" : ""}
                          >
                            Payments
                          </DropdownItem>
                        </DropdownMenu>
                      </UncontrolledDropdown>
                    </li>
                  </ul>
                </div>
                <div className={`search-wrap ${searchToggle ? "active" : ""}`}>
                  <div className="search-content">
                    <a
                      href="#item"
                      onClick={(ev) => {
                        ev.preventDefault();
                        toggleSearch();
                      }}
                      className="search-back btn btn-icon toggle-search"
                    >
                      <Icon name="arrow-left"></Icon>
                    </a>
                    <input
                      ref={searchInputRef}
                      type="text"
                      className="form-control border-transparent form-focus-none"
                      placeholder="Search"
                      value={searchText}
                      onChange={(e) => onSearchChange(e)}
                      onBlur={handleSearchBlur}
                    />
                    <button className="search-submit btn btn-icon">
                      <Icon name="search"></Icon>
                    </button>
                  </div>
                </div>
              </div>

              {/* Selected items info */}
              {selectedIds.size > 0 && (
                <Alert color="info" className="m-3 py-2">
                  <div className="d-flex justify-content-between align-items-center">
                    <span style={{ fontSize: "0.875rem" }}>
                      {selectedIds.size} notification(s) selected
                    </span>
                  </div>
                </Alert>
              )}

              <React.Fragment>
                <SimpleBar className="nk-ibx-list">
                  {tabData.length > 0 ? (
                    tabData.map((item: any) => {
                      return (
                        <>
                          <div
                            className={`nk-ibx-item ${
                              item.viewed ? "" : "is-unread"
                            }`}
                          >
                            <a
                              href="#item"
                              onClick={(ev) => {
                                onListClick(ev, item);
                              }}
                              className="nk-ibx-link current"
                            >
                              <span></span>
                            </a>
                            <div className="nk-ibx-item-elem nk-ibx-item-check">
                              <div className="custom-control custom-control-sm custom-checkbox">
                                <input
                                  type="checkbox"
                                  checked={selectedIds.has(item.notificationId)}
                                  className="custom-control-input nk-dt-item-check"
                                  id={`notification-${item.notificationId}`}
                                  onChange={(e) =>
                                    handleCheckboxChange(
                                      item.notificationId,
                                      e.target.checked
                                    )
                                  }
                                />
                                <label
                                  className="custom-control-label"
                                  htmlFor={`notification-${item.notificationId}`}
                                ></label>
                              </div>
                            </div>
                            <div
                              className="nk-ibx-item-elem nk-ibx-item"
                              style={{ background: "none", border: "none" }}
                            >
                              <div className="sub-text">{item.senderName}</div>
                            </div>
                            <div
                              className="nk-ibx-item-elem nk-ibx-item-fluid"
                              style={{ fontSize: " 0.875rem" }}
                            >
                              <div className="nk-ibx-context-group">
                                <div className="nk-ibx-context-badges">
                                  <Badge
                                    color={
                                      moduleColors[item.moduleName] ||
                                      moduleColors.Default
                                    }
                                  >
                                    {item.moduleName}
                                  </Badge>
                                </div>
                                <div className="nk-ibx-context">
                                  <span className="nk-ibx-context-text">
                                    <span className="heading">
                                      {item.messageTitle}
                                    </span>{" "}
                                  </span>
                                </div>
                              </div>
                            </div>
                            <div className="nk-ibx-item-elem nk-ibx-item-time">
                              <div
                                className="sub-text"
                                style={{ whiteSpace: "nowrap" }}
                              >
                                {item.createdAt &&
                                  dayjs(item.createdAt).format(
                                    "DD-MM-YYYY HH:mm"
                                  )}
                              </div>
                            </div>
                            <div className="nk-ibx-item-elem nk-ibx-item-tools">
                              <div className="ibx-actions">
                                <ul className="ibx-actions-visible gx-2">
                                  <li>
                                    <UncontrolledDropdown>
                                      <DropdownToggle
                                        tag={"a"}
                                        href="#item"
                                        onClick={(ev) => ev.preventDefault()}
                                        className="dropdown-toggle btn btn-sm btn-icon btn-trigger"
                                      >
                                        <Icon name="more-h"></Icon>
                                      </DropdownToggle>
                                      <DropdownMenu end>
                                        <ul className="link-list-opt no-bdr">
                                          <li>
                                            <DropdownItem
                                              tag="a"
                                              href="#item"
                                              onClick={(ev) => {
                                                ev.preventDefault();
                                                // toggleMessageView();
                                                // setMailId(item.id);
                                              }}
                                            >
                                              <Icon name="eye"></Icon>
                                              <span>View</span>
                                            </DropdownItem>
                                          </li>
                                          <li>
                                            <DropdownItem
                                              tag="a"
                                              href="#item"
                                              onClick={(ev) => {
                                                ev.preventDefault();
                                                deleteNotification(
                                                  item.notificationId
                                                );
                                              }}
                                            >
                                              <Icon name="trash"></Icon>
                                              <span>Delete</span>
                                            </DropdownItem>
                                          </li>
                                        </ul>
                                      </DropdownMenu>
                                    </UncontrolledDropdown>
                                  </li>
                                </ul>
                              </div>
                            </div>
                          </div>
                        </>
                      );
                    })
                  ) : (
                    <div
                      style={{
                        display: "flex",
                        justifyContent: "center",
                        alignItems: "center",
                        height: "100%",
                      }}
                    >
                      No Data
                    </div>
                  )}
                </SimpleBar>
              </React.Fragment>
            </React.Fragment>
          )}
        </>
      )}
    </div>
  );
};

export default NotificationList;
